/**
 * API Service for Candidate Resume Service
 * Handles resume upload, parsing, and session management
 * Base URL: Configurable via VITE_CANDIDATE_SERVICE_URL
 */

import axios from 'axios';
import { handleApiError } from '../../shared/utils/errorHandler';

const CANDIDATE_SERVICE_BASE_URL = import.meta.env.VITE_CANDIDATE_SERVICE_URL || 'http://localhost:8002';

export interface SessionUploadResponse {
    success: boolean;
    message: string;
    payload?: {
        session_id: string;
        progress: {
            session_id: string;
            status: string;
            current_step: number;
            total_steps: number;
            progress_percentage: number;
            questions: {
                total: number;
                answered: number;
                skipped: number;
                pending: number;
                required_pending: number;
                remaining: number;
            };
            can_complete: boolean;
            created_at: string;
            updated_at: string;
        };
        missing_questions: Array<{
            id: number;
            question: string;
        }>;
        parsed_data_summary: {
            name: string;
            email: string;
            phone: string;
            skills_count: number;
            experience_years: number;
        };
    };
    error?: string;
}

export interface SessionDetailsResponse {
    success: boolean;
    message: string;
    payload?: {
        session: {
            session_id: string;
            status: string;
            user_id: string;
            email: string;
            created_at: string;
            updated_at: string;
            expires_at: string;
            candidate_id: string | null;
            error_message: string | null;
        };
        progress: any;
        remaining_questions: Array<{
            id: number;
            question: string;
            is_required: boolean;
        }>;
        all_questions: Array<{
            id: number;
            question: string;
            status: string;
            answer: string | null;
            answered_at: string | null;
            is_required: boolean;
        }>;
        completion_stats: {
            total: number;
            answered: number;
            skipped: number;
            pending: number;
            required_pending: number;
        };
    };
    error?: string;
}

export interface AnswerQuestionResponse {
    success: boolean;
    message: string;
    payload?: {
        progress: any;
        remaining_questions: any[];
        all_questions: any[];
        completion_stats: any;
        can_complete: boolean;
    };
    error?: string;
}

export interface CompleteSessionResponse {
    success: boolean;
    message: string;
    payload?: {
        candidate_id: string;
        session_completed: boolean;
        candidate_summary: {
            name: string;
            email: string;
            phone: string;
            total_experience_years: number;
            education_count: number;
            skills_count: number;
            certifications_count: number;
            projects_count: number;
        };
    };
    error?: string;
}

export interface CandidateProfile {
    // Backend fields (as returned from candidate-service)
    id: string; // MongoDB ID (returned as "id" from backend)
    supabaseUserId: string; // Supabase user ID
    email: string;
    firstName?: string;
    lastName?: string;
    phone?: string;
    resumeUrl?: string;
    resumeS3Key?: string;
    linkedInUrl?: string;
    parsedResume?: any;
    missingContentQuestions?: any[];
    preferences?: any;
    profileCompleted?: boolean;
    isActive?: boolean;
    resumeStatus?: string;
    createdAt?: string;
    updatedAt?: string;
    profileViews?: number;
    // Legacy/alias fields for backwards compatibility
    _id?: string; // Alias for id
    candidate_id?: string; // Alias for supabaseUserId
    name?: string; // Computed from firstName + lastName
    skills?: string[];
    experience?: any;
    education?: any[];
    certifications?: any[];
    projects?: any[];
    work_experience?: any[];
    languages?: any[];
    summary?: string;
    location?: string;
    linkedin_url?: string;
    portfolio_url?: string;
    github_url?: string;
    resume_data?: any;
    created_at?: string;
    updated_at?: string;
}

export interface GetCandidateProfileResponse {
    success: boolean;
    message: string;
    candidate?: CandidateProfile;
    error?: string;
}

class CandidateServiceApi {
    /**
     * Upload resume and start a new parsing session
     */
    async uploadResume(
        file: File,
        candidateId: string,
        email?: string
    ): Promise<SessionUploadResponse> {
        try {
            const formData = new FormData();
            formData.append('resume', file);
            formData.append('candidate_id', candidateId);  // Note: snake_case for session endpoints
            if (email) {
                formData.append('email', email);
            }

            const response = await axios.post<SessionUploadResponse>(
                `${CANDIDATE_SERVICE_BASE_URL}/onboarding/v1/session/upload`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );

            return response.data;
        } catch (error: any) {
            const serviceError = handleApiError(error, 'candidate');
            throw serviceError;
        }
    }

    /**
     * Get session details and progress
     */
    async getSession(sessionId: string): Promise<SessionDetailsResponse> {
        try {
            const response = await axios.get<SessionDetailsResponse>(
                `${CANDIDATE_SERVICE_BASE_URL}/onboarding/v1/session/${sessionId}`
            );

            return response.data;
        } catch (error: any) {
            const serviceError = handleApiError(error, 'candidate');
            throw serviceError;
        }
    }

    /**
     * Answer a missing information question
     */
    async answerQuestion(
        sessionId: string,
        questionId: number,
        answer: string
    ): Promise<AnswerQuestionResponse> {
        try {
            const response = await axios.post<AnswerQuestionResponse>(
                `${CANDIDATE_SERVICE_BASE_URL}/onboarding/v1/session/${sessionId}/answer`,
                {
                    question_id: questionId,  // Note: snake_case for session endpoints
                    answer: answer,
                }
            );

            return response.data;
        } catch (error: any) {
            const serviceError = handleApiError(error, 'candidate');
            throw serviceError;
        }
    }

    /**
     * Skip a question
     */
    async skipQuestion(
        sessionId: string,
        questionId: number
    ): Promise<AnswerQuestionResponse> {
        try {
            const response = await axios.post<AnswerQuestionResponse>(
                `${CANDIDATE_SERVICE_BASE_URL}/onboarding/v1/session/${sessionId}/skip`,
                {
                    question_id: questionId,  // Note: snake_case for session endpoints
                }
            );

            return response.data;
        } catch (error: any) {
            const serviceError = handleApiError(error, 'candidate');
            throw serviceError;
        }
    }

    /**
     * Complete the session and create/update candidate profile
     */
    async completeSession(sessionId: string): Promise<CompleteSessionResponse> {
        try {
            const response = await axios.post<CompleteSessionResponse>(
                `${CANDIDATE_SERVICE_BASE_URL}/onboarding/v1/session/${sessionId}/complete`
            );

            return response.data;
        } catch (error: any) {
            const serviceError = handleApiError(error, 'candidate');
            throw serviceError;
        }
    }

    /**
     * Get user's sessions by user_id or email
     */
    async getUserSessions(userId?: string, email?: string): Promise<any> {
        try {
            const params = new URLSearchParams();
            if (userId) params.append('user_id', userId);  // Note: snake_case
            if (email) params.append('email', email);

            const response = await axios.get(
                `${CANDIDATE_SERVICE_BASE_URL}/onboarding/v1/session/user?${params.toString()}`
            );

            return response.data;
        } catch (error: any) {
            const serviceError = handleApiError(error, 'candidate');
            throw serviceError;
        }
    }

    /**
     * Cancel and delete a session
     */
    async cancelSession(sessionId: string): Promise<{ success: boolean; message: string }> {
        try {
            const response = await axios.delete(
                `${CANDIDATE_SERVICE_BASE_URL}/onboarding/v1/session/${sessionId}`
            );

            return response.data;
        } catch (error: any) {
            const serviceError = handleApiError(error, 'candidate');
            throw serviceError;
        }
    }

    /**
     * Get complete candidate profile for current authenticated user
     * Uses the /me endpoint which extracts user ID from JWT token
     * This is the preferred method - no need to pass IDs explicitly
     */
    async getCandidateProfile(): Promise<GetCandidateProfileResponse> {
        try {
            const token = localStorage.getItem('authToken');
            if (!token) {
                return {
                    success: false,
                    message: 'Not authenticated',
                    error: 'No auth token found'
                };
            }

            const response = await axios.get(
                `${CANDIDATE_SERVICE_BASE_URL}/candidate/v1/me`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );

            // Backend returns { payload: {...}, status: { msg: "", error: false } }
            const backendData = response.data;


            if (backendData.payload) {
                return {
                    success: true,
                    message: 'Success',
                    candidate: backendData.payload
                };
            } else if (backendData.status && backendData.status.error) {
                return {
                    success: false,
                    message: backendData.status.msg || 'Failed to fetch candidate profile',
                    error: backendData.error || 'Unknown error'
                };
            } else {
                return {
                    success: false,
                    message: 'Failed to fetch candidate profile',
                    error: 'Unknown error'
                };
            }
        } catch (error: any) {
            const serviceError = handleApiError(error, 'candidate');
            console.error('Failed to fetch candidate profile:', serviceError.message);
            throw serviceError;
        }
    }

    /**
     * Update candidate preferences
     * Updates the preferences directly in the request body (not nested)
     * Uses /me endpoint which extracts user from JWT token
     */
    async updateCandidatePreferences(
        _supabaseUserId: string, // Kept for backward compatibility but not used
        preferences: {
            job_type?: string[];
            work_location?: string;
            salary_expectation?: string;
            preferred_locations?: string[];
            remote_work?: boolean;
            compensation_packages?: string[];
            desired_job_titles?: string[];
            target_companies?: string[];
        }
    ): Promise<{ success: boolean; message: string; error?: string }> {
        try {
            const token = localStorage.getItem('authToken');
            if (!token) {
                return {
                    success: false,
                    message: 'No auth token found',
                    error: 'No auth token found'
                };
            }

            // Use /me endpoint - extracts user from JWT token
            const response = await axios.patch(
                `${CANDIDATE_SERVICE_BASE_URL}/onboarding/v1/candidate/me/preferences`,
                preferences,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );

            return response.data;
        } catch (error: any) {
            const serviceError = handleApiError(error, 'candidate');
            console.error('Failed to update candidate preferences:', serviceError.message);
            throw serviceError;
        }
    }

    /**
     * Update career preferences (extended version)
     * Supports locations, compensation, work type, job titles, availability, and free-form text
     * Uses /me endpoint which extracts user from JWT token
     */
    async updateCareerPreferences(
        _supabaseUserId: string, // Kept for backward compatibility but not used
        preferences: {
            cities?: string[];
            minSalary?: number;
            maxSalary?: number;
            workType?: string;
            jobTitles?: string[];
            startDate?: string;
            lookingFor?: string;
        }
    ): Promise<{ success: boolean; message: string; error?: string }> {
        try {
            const token = localStorage.getItem('authToken');
            if (!token) {
                return {
                    success: false,
                    message: 'No auth token found',
                    error: 'No auth token found'
                };
            }

            // Use /me endpoint - extracts user from JWT token
            const response = await axios.patch(
                `${CANDIDATE_SERVICE_BASE_URL}/onboarding/v1/candidate/me/preferences`,
                preferences,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );

            return response.data;
        } catch (error: any) {
            const serviceError = handleApiError(error, 'candidate');
            console.error('Failed to update career preferences:', serviceError.message);
            throw serviceError;
        }
    }

    /**
     * Update parsed resume data
     * Updates experience, education, and certifications in parsed_resume.raw_json
     * Uses MongoDB ObjectID as the parameter
     */
    async updateParsedResume(
        candidateMongoId: string,
        updates: {
            experience?: any[];
            education?: any[];
            certifications?: any[];
        }
    ): Promise<{ success: boolean; message: string; error?: string }> {
        try {
            // Build the update object for MongoDB with dot notation
            const updateData: any = {};

            if (updates.experience !== undefined) {
                updateData['parsed_resume.raw_json.experience.work'] = updates.experience;
            }
            if (updates.education !== undefined) {
                updateData['parsed_resume.raw_json.education.education'] = updates.education;
            }
            if (updates.certifications !== undefined) {
                updateData['parsed_resume.raw_json.certification.certificates'] = updates.certifications;
            }

            const response = await axios.patch(
                `${CANDIDATE_SERVICE_BASE_URL}/candidate/v1/${candidateMongoId}`,
                updateData
            );

            // Backend returns { payload: {...}, status: { msg: "", error: false } }
            const backendData = response.data;

            if (backendData.status && !backendData.status.error) {
                return {
                    success: true,
                    message: 'Profile updated successfully'
                };
            } else {
                return {
                    success: false,
                    message: backendData.status?.msg || 'Failed to update profile',
                    error: backendData.error || 'Unknown error'
                };
            }
        } catch (error: any) {
            const serviceError = handleApiError(error, 'candidate');
            console.error('Failed to update parsed resume:', serviceError.message);
            throw serviceError;
        }
    }

    /**
     * Update profile data for current user (uses /me endpoint)
     * Comprehensive method to update any profile field on My Profile page
     * Extracts candidate from JWT token - no ID needed
     *
     * NOTE: Per Redesigned_embedding_matching_strategy.md:
     * - Experience/Projects changes → Resume/Skills/Summary embedding regeneration
     * - Education changes → Resume embedding regeneration
     * - Skills changes → Skills embedding regeneration
     * - Summary changes → Summary embedding regeneration
     * - Professional Links → No embedding change
     * (Backend embedding triggers TBD when matches-vectors service is updated)
     */
    async updateProfileMe(
        updates: {
            // Parsed resume fields (nested in parsed_resume.raw_json)
            experience?: any[];
            education?: any[];
            certifications?: any[];
            summary?: string;
            skills?: string[];
            // Professional links (structured object)
            professionalLinks?: {
                linkedin?: string;
                twitter?: string;
                portfolio?: string;
                others?: Array<{ name: string; url: string }>;
            };
            // Career preferences (triggers preferences embedding regeneration)
            preferences?: {
                cities?: string[];
                minSalary?: number;
                maxSalary?: number;
                workType?: string;
                jobTitles?: string[];
                startDate?: string;
                lookingFor?: string;
            };
        }
    ): Promise<{ success: boolean; message: string; error?: string }> {
        try {
            const token = localStorage.getItem('authToken');
            if (!token) {
                throw new Error('No auth token found');
            }

            // Build the update object for MongoDB with dot notation
            // Per plan: Write to parsed_resume.* structured fields (NOT raw_json)
            // raw_json is backup only, updated during resume upload, never during manual edits
            const updateData: any = {};

            // Parsed resume fields - use structured paths (source of truth)
            if (updates.experience !== undefined) {
                updateData['parsed_resume.experience'] = updates.experience;
            }
            if (updates.education !== undefined) {
                updateData['parsed_resume.education'] = updates.education;
            }
            if (updates.certifications !== undefined) {
                updateData['parsed_resume.certifications'] = updates.certifications;
            }
            if (updates.summary !== undefined) {
                updateData['parsed_resume.summary'] = updates.summary;
            }
            if (updates.skills !== undefined) {
                updateData['parsed_resume.skills'] = updates.skills;
            }

            // Professional links (stored as nested object)
            if (updates.professionalLinks !== undefined) {
                if (updates.professionalLinks.linkedin !== undefined) {
                    updateData['professional_links.linkedin'] = updates.professionalLinks.linkedin;
                }
                if (updates.professionalLinks.twitter !== undefined) {
                    updateData['professional_links.twitter'] = updates.professionalLinks.twitter;
                }
                if (updates.professionalLinks.portfolio !== undefined) {
                    updateData['professional_links.portfolio'] = updates.professionalLinks.portfolio;
                }
                if (updates.professionalLinks.others !== undefined) {
                    updateData['professional_links.others'] = updates.professionalLinks.others;
                }
            }

            // Career preferences (triggers preferences embedding regeneration)
            if (updates.preferences !== undefined) {
                updateData['preferences'] = updates.preferences;
            }

            const response = await axios.patch(
                `${CANDIDATE_SERVICE_BASE_URL}/candidate/v1/me`,
                updateData,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );

            // Backend returns { message: "Candidate updated successfully" }
            return {
                success: true,
                message: 'Profile updated successfully'
            };
        } catch (error: any) {
            const serviceError = handleApiError(error, 'candidate');
            console.error('❌ Failed to update profile via /me:', serviceError.message);
            throw serviceError;
        }
    }

    /**
     * Get presigned URL for a candidate's resume
     * Used by recruiters to view candidate resumes from applications
     * @param userId - MongoDB User ID (from m_users)
     * @returns Presigned URL valid for 60 minutes
     */
    async getResumePresignedUrl(userId: string): Promise<{ success: boolean; presignedUrl?: string; error?: string }> {
        try {
            const token = localStorage.getItem('authToken');
            if (!token) {
                return {
                    success: false,
                    error: 'No auth token found'
                };
            }

            const response = await axios.get(
                `${CANDIDATE_SERVICE_BASE_URL}/candidate/v1/user/${userId}/resume`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );

            const backendData = response.data;

            if (backendData.payload?.presignedUrl) {
                return {
                    success: true,
                    presignedUrl: backendData.payload.presignedUrl
                };
            } else {
                return {
                    success: false,
                    error: backendData.status?.msg || 'Failed to get resume URL'
                };
            }
        } catch (error: any) {
            console.error('Failed to get resume presigned URL:', error);
            return {
                success: false,
                error: error.response?.data?.status?.msg || error.message || 'Failed to get resume URL'
            };
        }
    }

    /**
     * Get candidate profile by MongoDB User ID
     * Used by recruiters to view candidate details from applications
     * Note: Application.candidateId is the MongoDB User ID (from m_users), not the candidate's _id
     */
    async getCandidateById(userId: string): Promise<GetCandidateProfileResponse> {
        try {
            const token = localStorage.getItem('authToken');
            if (!token) {
                return {
                    success: false,
                    message: 'Not authenticated',
                    error: 'No auth token found'
                };
            }

            const response = await axios.get(
                `${CANDIDATE_SERVICE_BASE_URL}/candidate/v1/user/${userId}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );

            // Backend returns { payload: {...}, status: { msg: "", error: false } }
            const backendData = response.data;

            if (backendData.payload) {
                return {
                    success: true,
                    message: 'Success',
                    candidate: backendData.payload
                };
            } else if (backendData.status && backendData.status.error) {
                return {
                    success: false,
                    message: backendData.status.msg || 'Failed to fetch candidate profile',
                    error: backendData.error || 'Unknown error'
                };
            } else {
                return {
                    success: false,
                    message: 'Failed to fetch candidate profile',
                    error: 'Unknown error'
                };
            }
        } catch (error: any) {
            const serviceError = handleApiError(error, 'candidate');
            console.error('Failed to fetch candidate by user ID:', serviceError.message);
            throw serviceError;
        }
    }

    /**
     * Update candidate profile
     * Generic update method for candidate profile fields
     */
    async updateProfile(
        candidateMongoId: string,
        updates: any
    ): Promise<{ success: boolean; message: string; error?: string }> {
        try {
            const response = await axios.patch(
                `${CANDIDATE_SERVICE_BASE_URL}/candidate/v1/${candidateMongoId}`,
                updates
            );

            const backendData = response.data;

            if (backendData.status && !backendData.status.error) {
                return {
                    success: true,
                    message: 'Profile updated successfully'
                };
            } else {
                return {
                    success: false,
                    message: backendData.status?.msg || 'Failed to update profile',
                    error: backendData.error || 'Unknown error'
                };
            }
        } catch (error: any) {
            const serviceError = handleApiError(error, 'candidate');
            console.error('Failed to update profile:', serviceError.message);
            throw serviceError;
        }
    }
}

export const candidateServiceApi = new CandidateServiceApi();
